import { useEffect } from 'react'
import { Routes, Route, useLocation, useParams, useNavigate, Navigate } from 'react-router-dom'
import Layout from './components/layout/Layout'
import AuthLayout from './components/layout/AuthLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { captureReferral, REFERRAL_PARAM } from './utils/referral'
import { setApiNavigator } from './services/api'

// Pages
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import DashboardPage from './pages/DashboardPage'
import FreightCalculatorPage from './pages/FreightCalculatorPage'
import ConsolidationPage from './pages/ConsolidationPage'
import ConsolidationOrderPage from './pages/ConsolidationOrderPage'
import CargoPage from './pages/CargoPage'
// CargoDetailPage 已于 2026-07-31 删除：报名详情并入 CargoPage 成为页内视图。
// /cargo/:bookingCode 现在只剩一条重定向（见下方 LegacyCargoDetailRedirect），
// 原因见 CargoPage.jsx 顶部说明。
// 散货板块已下线（2026-07-30），以下 import 随路由一并注释。
// 代码文件全部保留，恢复时取消注释即可。详见 components/layout/Navbar.jsx 的说明。
// 以下四个属于已下线的「散货板块」，随路由一并注释：
// import CargoSignPage from './pages/CargoSignPage'
// import CargoSearchPage from './pages/CargoSearchPage'
// import CargoClaimPage from './pages/CargoClaimPage'
// import LCLPage from './pages/LCLPage'
// 这两页在线，2026-07-31 起归入新建的「我的」板块（原「订单板块」已撤销）。
// 其中「订单下单」的拼柜 Tab 承接了原散货拼柜下单能力
import MyOrdersPage from './pages/MyOrdersPage'
import OrderCreatePage from './pages/OrderCreatePage'
import TrackingPage from './pages/TrackingPage'
import FinancePage from './pages/FinancePage'
import IssuesPage from './pages/IssuesPage'
import AboutPage from './pages/AboutPage'
import NotFoundPage from './pages/NotFoundPage'
import InspectionPage from './pages/InspectionPage'
import NotificationsPage from './pages/NotificationsPage'
// import FreightEvaluationPage from './pages/FreightEvaluationPage'  // 散货板块已下线
import PlaceholderPage from './pages/PlaceholderPage'

/**
 * 旧报名详情地址 /cargo/<单号> 的重定向兜底。
 *
 * 该页已于 2026-07-31 并入「我的报名」页内视图、路由随之删除，但地址可能被人收藏过
 * 或发在别处，直接落 404 不友好。这里把单号原样带成 ?code=，「我的报名」会按它定位到
 * 对应行；用 replace 是为了不在历史里留一条，避免点返回又弹回这个已废弃的地址。
 *
 * 注意：这只是兼容旧链接，不代表 /cargo/<单号> 还是一个可分享的详情地址 ——
 * 落地页仍要求登录（看自己的单）或校验手机号后四位（看他人的单）。
 */
function LegacyCargoDetailRedirect() {
  const { bookingCode } = useParams()
  return <Navigate to={`/my-consolidation?code=${encodeURIComponent(bookingCode || '')}`} replace />
}

function App() {
  const location = useLocation()
  const navigate = useNavigate()

  // 把路由跳转能力交给 api.js 的 401 处理用：它不是组件、拿不到 useNavigate，
  // 没有它就只能 window.location.href 整页重载。
  useEffect(() => {
    setApiNavigator(navigate)
    return () => setApiNavigator(null)
  }, [navigate])

  // 渠道归因捕获。放在 App 内而不是 Layout 内 ——
  // Layout 与 AuthLayout 是平级的，且 * 404 路由在两者之外；
  // 放 Layout 会漏掉 /signup、/login，以及「链接打错落到 404」这三种场景，
  // 而最后一种恰恰最该抓住。App 渲染在 BrowserRouter 内（见 main.jsx），
  // 所以一个调用点覆盖全部路由。
  useEffect(() => {
    if (!captureReferral(location.search, location.pathname)) return
    // 抹掉地址栏参数：用 replaceState 而不是 navigate ——
    // navigate 会触发路由变更，而本 effect 依赖 search，会形成循环。
    try {
      const params = new URLSearchParams(location.search)
      params.delete(REFERRAL_PARAM)
      const qs = params.toString()
      window.history.replaceState({}, '', location.pathname + (qs ? `?${qs}` : ''))
    } catch {
      /* 抹除失败不影响归因本身 */
    }
  }, [location.search, location.pathname])

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/freight-calculator" element={<FreightCalculatorPage />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/consolidation" element={<ConsolidationPage />} />
        <Route path="/consolidation-order" element={<ConsolidationOrderPage />} />
        <Route path="/inspection" element={<InspectionPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* 散货板块已下线（2026-07-30）：连路由一起关掉，避免直接输 URL 仍能进。
            页面组件全部保留在 src/pages/ 下，恢复时把这 5 行和顶部对应 import
            一起取消注释即可。注意 /lcl-tracking 复用的 TrackingPage 仍在服役
            （集运板块的 /tracking 用它），不要连它一起删。

            重要：散货「拼柜下单」能力并未随本板块下线，它已迁入下面的
            「我的 > 订单下单」（2026-07-31 起该页归入新建的「我的」板块，
            原「订单板块」已撤销）—— /order-create 的拼柜 Tab 走 OrderCreatePage ->
            components/order/LclOrderModule。所以 /lcl 这个旧入口关掉不影响下单，
            用户改走「我的」板块即可。 */}
        {/* <Route path="/freight-evaluation" element={<FreightEvaluationPage />} /> */}
        {/* <Route path="/lcl" element={<LCLPage />} /> */}
        {/* <Route path="/cargo-search" element={<CargoSearchPage />} /> */}
        {/* <Route path="/lcl-tracking" element={<TrackingPage />} /> */}
        {/* <Route path="/cargo-claim" element={<CargoClaimPage />} /> */}
        <Route path="/order-create" element={<OrderCreatePage />} />
        <Route path="/my-orders" element={<MyOrdersPage />} />
        <Route path="/finance/payment" element={<PlaceholderPage />} />
        <Route path="/finance/recharge" element={<PlaceholderPage />} />
        <Route path="/finance/reconciliation" element={<PlaceholderPage />} />
        <Route path="/finance/transactions" element={<PlaceholderPage />} />
        <Route path="/finance/payment-records" element={<PlaceholderPage />} />
        <Route path="/finance/collection-service" element={<PlaceholderPage />} />
        <Route path="/finance/financing-service" element={<PlaceholderPage />} />
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/cargo" element={<CargoPage />} />
          <Route path="/my-consolidation" element={<CargoPage />} />
          {/* 旧详情地址兼容，见文件顶部 LegacyCargoDetailRedirect */}
          <Route path="/cargo/:bookingCode" element={<LegacyCargoDetailRedirect />} />
          {/* 签收确认属于已下线的「散货板块」，页面不再需要，路由一并关闭。
              页面组件仍保留在 src/pages/CargoSignPage.jsx，恢复时取消本行与顶部 import 注释。 */}
          {/* <Route path="/cargo-sign" element={<CargoSignPage />} /> */}
          <Route path="/finance" element={<FinancePage />} />
          <Route path="/portal/finance" element={<FinancePage />} />
          <Route path="/issues" element={<IssuesPage />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
